import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearchPlus, faSearchMinus, faExpand, faCompress } from '@fortawesome/free-solid-svg-icons';

const CARD_WIDTH = 230;
const CARD_HEIGHT = 68;
const CARD_PAD = 14;
const CENTER_WIDTH_RATIO = 0.10;
const LANE_WIDTH_RATIO = 0.45;
const PROXIMITY_MS = 10 * 60 * 1000; // 10 minutes
const MINIMAP_W = 150;
const MINIMAP_H = 100;

// Check if two items share technique or tactic relevance
function hasRelevance(red, blue) {
  const redTech = red.technique;
  const blueTech = blue.findingData?.suspectedTechnique;
  if (!redTech && !blueTech) return false;

  // Same technique ID
  if (redTech?.id && blueTech?.id && redTech.id === blueTech.id) return true;

  // Same tactic
  if (redTech?.tactic && blueTech?.tactic && redTech.tactic === blueTech.tactic) return true;

  // Check if technique name words overlap
  const redWords = new Set(
    ((redTech?.name || '') + ' ' + (red.title || '')).toLowerCase().split(/\s+/).filter(w => w.length > 3)
  );
  const blueWords = ((blueTech?.name || '') + ' ' + (blue.title || '')).toLowerCase().split(/\s+/).filter(w => w.length > 3);
  for (const w of blueWords) {
    if (redWords.has(w)) return true;
  }

  return false;
}

// Normalize ID to string for comparison
function idStr(id) {
  if (!id) return '';
  if (typeof id === 'object' && id._id) return String(id._id);
  return String(id);
}

function formatDelta(ms) {
  if (ms < 1000) return `${ms}ms`;
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
}

const CorrelationTimeline = ({
  timeline,
  suggestions,
  onCorrelate,
  onUncorrelate,
  onActionClick,
  onFindingClick,
  darkMode,
  t,
  fullscreen,
  onToggleFullscreen
}) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const zoomRef = useRef(null);
  const transformRef = useRef(null); // persist zoom transform across redraws
  const [dimensions, setDimensions] = useState({ width: 900, height: 600 });

  // ResizeObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const { width } = el.getBoundingClientRect();
      if (width > 100) {
        const h = fullscreen
          ? window.innerHeight - 80
          : Math.max(500, window.innerHeight - 380);
        setDimensions({ width: width - 4, height: h });
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, [fullscreen]);

  const drawTimeline = useCallback(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    if (!timeline || timeline.length === 0) return;

    const { width, height } = dimensions;

    // --- Data preprocessing ---
    const redItems = [];
    const blueItems = [];
    const webItems = [];

    timeline.forEach(item => {
      const ts = new Date(item.timestamp).getTime();
      if (isNaN(ts)) return;
      const entry = { ...item, ts, _sid: idStr(item.id) };
      if (item.type === 'red_team') redItems.push(entry);
      else if (item.type === 'blue_team') blueItems.push(entry);
      else if (item.type === 'web_test') webItems.push(entry);
    });

    const hasWebTests = webItems.length > 0;

    if (redItems.length === 0 && blueItems.length === 0 && webItems.length === 0) return;

    // Build confirmed correlation map: blueSid → redSid
    const confirmedMap = new Map();
    blueItems.forEach(blue => {
      if (blue.relatedAction) {
        const redSid = idStr(blue.relatedAction);
        confirmedMap.set(blue._sid, redSid);
      }
    });

    // Build suggestion map: `${blueSid}-${redSid}` → { score, reasons, findingId, actionId }
    const suggestionMap = new Map();
    if (suggestions) {
      suggestions.forEach(s => {
        const blueSid = idStr(s.finding._id);
        s.suggestions?.forEach(sug => {
          const redSid = idStr(sug.action._id);
          suggestionMap.set(`${blueSid}-${redSid}`, {
            score: sug.score,
            reasons: sug.reasons || [],
            findingId: String(s.finding._id),
            actionId: String(sug.action._id)
          });
        });
      });
    }

    // Build correlation links
    const correlationLinks = [];
    const linkedBlueSids = new Set();
    const linkedRedSids = new Set();

    // 1. Confirmed correlations
    confirmedMap.forEach((redSid, blueSid) => {
      const red = redItems.find(r => r._sid === redSid);
      const blue = blueItems.find(b => b._sid === blueSid);
      if (red && blue) {
        correlationLinks.push({ type: 'confirmed', red, blue, findingId: blueSid });
        linkedBlueSids.add(blueSid);
        linkedRedSids.add(redSid);
      }
    });

    // 2. Suggested correlations
    suggestionMap.forEach((sug) => {
      const blueSid = idStr(sug.findingId);
      const redSid = idStr(sug.actionId);
      if (linkedBlueSids.has(blueSid) && linkedRedSids.has(redSid)) return;
      const red = redItems.find(r => r._sid === redSid || idStr(r.actionData?._id) === redSid);
      const blue = blueItems.find(b => b._sid === blueSid || idStr(b.findingData?._id) === blueSid);
      if (red && blue) {
        correlationLinks.push({
          type: 'suggested', red, blue,
          score: sug.score,
          reasons: sug.reasons,
          findingId: sug.findingId,
          actionId: sug.actionId
        });
        linkedBlueSids.add(blue._sid);
        linkedRedSids.add(red._sid);
      }
    });

    // 3. Time-proximity for unlinked items — only if they share technique/tactic relevance
    blueItems.forEach(blue => {
      if (linkedBlueSids.has(blue._sid)) return;
      for (const red of redItems) {
        if (linkedRedSids.has(red._sid)) continue;
        if (Math.abs(blue.ts - red.ts) <= PROXIMITY_MS && hasRelevance(red, blue)) {
          correlationLinks.push({
            type: 'time-proximity', red, blue,
            timeDelta: Math.abs(blue.ts - red.ts)
          });
          linkedBlueSids.add(blue._sid);
          linkedRedSids.add(red._sid);
          break;
        }
      }
    });

    // --- Layout ---
    const allTs = [...redItems, ...blueItems, ...webItems].map(i => i.ts);
    const minTs = d3.min(allTs);
    const maxTs = d3.max(allTs);
    const timeRange = maxTs - minTs || 3600000;

    const allCount = redItems.length + blueItems.length + webItems.length;
    const contentHeight = Math.max(height, allCount * (CARD_HEIGHT + CARD_PAD) * 0.55 + 120);

    const margin = { top: 40, bottom: 40 };
    const yScale = d3.scaleTime()
      .domain([new Date(maxTs + timeRange * 0.03), new Date(minTs - timeRange * 0.03)])
      .range([margin.top, contentHeight - margin.bottom]);

    // Adjust lane widths when web tests are present (3-lane layout)
    const redLaneRatio = hasWebTests ? 0.33 : LANE_WIDTH_RATIO;
    const blueLaneRatio = hasWebTests ? 0.33 : LANE_WIDTH_RATIO;
    const centerRatio = hasWebTests ? 0.05 : CENTER_WIDTH_RATIO;
    const center2Ratio = hasWebTests ? 0.05 : 0;

    const laneRight = width * redLaneRatio;
    const centerStart = laneRight;
    const centerEnd = centerStart + width * centerRatio;
    const blueLaneLeft = centerEnd;
    const blueLaneRight = blueLaneLeft + width * blueLaneRatio;
    const center2Start = blueLaneRight;
    const center2End = center2Start + width * center2Ratio;
    const webLaneLeft = hasWebTests ? center2End : width;
    const centerX = (centerStart + centerEnd) / 2;

    // Position cards
    const positionCards = (items, laneStart, laneEnd, alignRight) => {
      const sorted = [...items].sort((a, b) => b.ts - a.ts);
      const positions = [];
      sorted.forEach(item => {
        let y = yScale(new Date(item.ts));
        const x = alignRight ? laneEnd - CARD_WIDTH - 10 : laneStart + 10;
        for (const pos of positions) {
          if (Math.abs(y - pos.y) < CARD_HEIGHT + CARD_PAD) {
            y = pos.y + CARD_HEIGHT + CARD_PAD;
          }
        }
        positions.push({ item, x, y, w: CARD_WIDTH, h: CARD_HEIGHT });
      });
      return positions;
    };

    const redPositions = positionCards(redItems, 0, laneRight, true);
    const bluePositions = positionCards(blueItems, blueLaneLeft, hasWebTests ? blueLaneRight : width, false);
    const webPositions = hasWebTests ? positionCards(webItems, webLaneLeft, width, false) : [];

    const posMap = new Map();
    redPositions.forEach(p => posMap.set(p.item._sid, p));
    bluePositions.forEach(p => posMap.set(p.item._sid, p));
    webPositions.forEach(p => posMap.set(p.item._sid, p));

    const maxY = Math.max(
      ...redPositions.map(p => p.y + p.h),
      ...bluePositions.map(p => p.y + p.h),
      ...(webPositions.length > 0 ? webPositions.map(p => p.y + p.h) : [0]),
      contentHeight
    );
    const finalHeight = maxY + 60;

    // --- SVG setup ---
    svg.attr('width', width).attr('height', height);

    // Background (captures zoom/pan events)
    svg.append('rect')
      .attr('width', width).attr('height', height)
      .attr('fill', darkMode ? '#1f2937' : '#f9fafb')
      .style('cursor', 'grab');

    // Clip path
    const defs = svg.append('defs');
    defs.append('clipPath').attr('id', 'tl-clip')
      .append('rect').attr('width', width).attr('height', height);

    const mainG = svg.append('g').attr('clip-path', 'url(#tl-clip)');
    const contentG = mainG.append('g').attr('class', 'content');

    // --- Zoom with filter: allow zoom on everything EXCEPT card groups ---
    const zoomBehavior = d3.zoom()
      .scaleExtent([0.2, 4])
      .filter((event) => {
        // Allow wheel zoom everywhere
        if (event.type === 'wheel') return true;
        // For pointer events (mousedown/touchstart), block zoom if target is inside a card group
        const target = event.target;
        if (target && target.closest && target.closest('.timeline-card')) return false;
        // Also check d3 parent chain for SVG elements
        let el = target;
        while (el && el !== svgRef.current) {
          if (el.classList && el.classList.contains('timeline-card')) return false;
          el = el.parentNode;
        }
        return true;
      })
      .on('zoom', (event) => {
        contentG.attr('transform', event.transform);
        transformRef.current = event.transform;
        updateMinimap(event.transform);
      });
    svg.call(zoomBehavior);
    zoomRef.current = zoomBehavior;

    // Large background inside contentG for pan on empty space
    contentG.append('rect')
      .attr('x', -500).attr('y', -500)
      .attr('width', width + 1000).attr('height', finalHeight + 1000)
      .attr('fill', 'transparent')
      .attr('pointer-events', 'all');

    // Lane backgrounds
    contentG.append('rect')
      .attr('x', 0).attr('y', 0)
      .attr('width', laneRight).attr('height', finalHeight)
      .attr('fill', darkMode ? 'rgba(239,68,68,0.04)' : 'rgba(239,68,68,0.03)');
    contentG.append('rect')
      .attr('x', blueLaneLeft).attr('y', 0)
      .attr('width', width - blueLaneLeft).attr('height', finalHeight)
      .attr('fill', darkMode ? 'rgba(59,130,246,0.04)' : 'rgba(59,130,246,0.03)');
    contentG.append('rect')
      .attr('x', centerStart).attr('y', 0)
      .attr('width', centerEnd - centerStart).attr('height', finalHeight)
      .attr('fill', darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)');

    // Web test lane (teal) - only when present
    if (hasWebTests) {
      contentG.append('rect')
        .attr('x', webLaneLeft).attr('y', 0)
        .attr('width', width - webLaneLeft).attr('height', finalHeight)
        .attr('fill', darkMode ? 'rgba(6,182,212,0.04)' : 'rgba(6,182,212,0.03)');
      contentG.append('rect')
        .attr('x', center2Start).attr('y', 0)
        .attr('width', center2End - center2Start).attr('height', finalHeight)
        .attr('fill', darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)');
    }

    // Time axis ticks
    const axisTicks = yScale.ticks(d3.timeHour.every(1)).length > 20
      ? yScale.ticks(d3.timeDay.every(1))
      : yScale.ticks(d3.timeHour.every(1)).length > 0
        ? yScale.ticks(d3.timeHour.every(1))
        : yScale.ticks(10);
    const formatTime = d3.timeFormat('%b %d, %H:%M');

    axisTicks.forEach(tick => {
      const y = yScale(tick);
      contentG.append('line')
        .attr('x1', 0).attr('x2', width)
        .attr('y1', y).attr('y2', y)
        .attr('stroke', darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)')
        .attr('stroke-dasharray', '2,4');
      contentG.append('text')
        .attr('x', centerX).attr('y', y - 4)
        .attr('text-anchor', 'middle')
        .attr('font-size', '9px')
        .attr('fill', darkMode ? '#6b7280' : '#9ca3af')
        .text(formatTime(tick));
    });

    contentG.append('line')
      .attr('x1', centerX).attr('x2', centerX)
      .attr('y1', 0).attr('y2', finalHeight)
      .attr('stroke', darkMode ? '#374151' : '#d1d5db');

    // --- Tooltip ---
    const tooltip = d3.select('body').append('div')
      .attr('class', 'corr-tl-tooltip')
      .style('position', 'absolute')
      .style('opacity', 0)
      .style('background', darkMode ? 'rgba(17,24,39,0.96)' : 'rgba(255,255,255,0.98)')
      .style('color', darkMode ? '#e5e7eb' : '#1f2937')
      .style('padding', '10px 14px')
      .style('border-radius', '8px')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .style('max-width', '320px')
      .style('box-shadow', '0 4px 12px rgba(0,0,0,0.25)')
      .style('border', darkMode ? '1px solid #374151' : '1px solid #e5e7eb')
      .style('z-index', '10000')
      .style('line-height', '1.5');

    let tooltipTimer = null;
    const showTooltip = (event, html) => {
      if (tooltipTimer) { clearTimeout(tooltipTimer); tooltipTimer = null; }
      tooltip.html(html)
        .style('left', (event.pageX + 14) + 'px')
        .style('top', (event.pageY - 12) + 'px')
        .transition().duration(120).style('opacity', 1);
    };
    const hideTooltip = () => {
      tooltipTimer = setTimeout(() => {
        tooltip.transition().duration(150).style('opacity', 0);
      }, 80);
    };

    // --- Drag line (visual indicator during drag-to-correlate) ---
    const dragLine = contentG.append('line')
      .attr('class', 'drag-line')
      .attr('stroke', '#a855f7')
      .attr('stroke-width', 2.5)
      .attr('stroke-dasharray', '6,4')
      .attr('opacity', 0)
      .attr('pointer-events', 'none');

    // Drop target highlight rectangle
    const dropHighlight = contentG.append('rect')
      .attr('class', 'drop-highlight')
      .attr('rx', 8)
      .attr('fill', 'none')
      .attr('stroke', '#a855f7')
      .attr('stroke-width', 3)
      .attr('stroke-dasharray', '4,4')
      .attr('opacity', 0)
      .attr('pointer-events', 'none');

    // --- Correlation paths ---
    const pathsG = contentG.append('g').attr('class', 'paths');

    correlationLinks.forEach(link => {
      const redPos = posMap.get(link.red._sid);
      const bluePos = posMap.get(link.blue._sid);
      if (!redPos || !bluePos) return;

      const x1 = redPos.x + redPos.w;
      const y1 = redPos.y + redPos.h / 2;
      const x2 = bluePos.x;
      const y2 = bluePos.y + bluePos.h / 2;
      const pathD = `M ${x1} ${y1} C ${centerX} ${y1}, ${centerX} ${y2}, ${x2} ${y2}`;

      let color, dash, sw;
      if (link.type === 'confirmed') { color = '#a855f7'; dash = 'none'; sw = 2.5; }
      else if (link.type === 'suggested') { color = '#f59e0b'; dash = '6,4'; sw = 2; }
      else { color = '#9ca3af'; dash = '2,4'; sw = 1; }

      const cssId = `corr-${link.red._sid}-${link.blue._sid}`.replace(/[^a-zA-Z0-9-]/g, '_');

      pathsG.append('path')
        .attr('d', pathD).attr('fill', 'none')
        .attr('stroke', color).attr('stroke-width', sw)
        .attr('stroke-dasharray', dash).attr('opacity', 0.6)
        .attr('class', cssId);

      // Fat invisible hit target
      const hitPath = pathsG.append('path')
        .attr('d', pathD).attr('fill', 'none')
        .attr('stroke', 'transparent').attr('stroke-width', 16)
        .style('cursor', (link.type === 'suggested' || link.type === 'confirmed') ? 'pointer' : 'default');

      hitPath
        .on('mouseover', (event) => {
          contentG.selectAll(`.${cssId}`).attr('opacity', 1).attr('stroke-width', sw + 2);
          let html = '';
          if (link.type === 'confirmed') {
            const delta = Math.abs(link.red.ts - link.blue.ts);
            html = `<div style="font-weight:600;color:#a855f7;margin-bottom:4px">${t('purpleTeam.confirmed') || 'Confirmed'}</div>`;
            html += `<div><b>${link.red.title}</b> &rarr; <b>${link.blue.title}</b></div>`;
            html += `<div style="margin-top:4px;color:${darkMode ? '#9ca3af' : '#6b7280'}">${t('purpleTeam.timeDelta') || 'Time delta'}: ${formatDelta(delta)}</div>`;
            html += `<div style="margin-top:6px;font-size:11px;color:#ef4444">${t('purpleTeam.clickToUnlink') || 'Click to unlink'}</div>`;
          } else if (link.type === 'suggested') {
            const delta = Math.abs(link.red.ts - link.blue.ts);
            html = `<div style="font-weight:600;color:#f59e0b;margin-bottom:4px">${t('purpleTeam.suggested') || 'Suggested'} (${link.score}%)</div>`;
            html += `<div><b>${link.red.title}</b> &rarr; <b>${link.blue.title}</b></div>`;
            if (link.reasons?.length) html += `<div style="margin-top:4px;color:${darkMode ? '#9ca3af' : '#6b7280'}">${t('purpleTeam.why') || 'Why:'} ${link.reasons.join(', ')}</div>`;
            html += `<div style="color:${darkMode ? '#9ca3af' : '#6b7280'}">${t('purpleTeam.timeDelta') || 'Time delta'}: ${formatDelta(delta)}</div>`;
            html += `<div style="margin-top:6px;font-size:11px;color:#f59e0b">${t('purpleTeam.clickToConfirm') || 'Click to confirm'}</div>`;
          } else {
            html = `<div style="font-weight:600;color:#9ca3af;margin-bottom:4px">${t('purpleTeam.timeProximity') || 'Time proximity'}</div>`;
            html += `<div><b>${link.red.title}</b> &rarr; <b>${link.blue.title}</b></div>`;
            html += `<div style="color:${darkMode ? '#9ca3af' : '#6b7280'}">Within ${formatDelta(link.timeDelta)}</div>`;
          }
          showTooltip(event, html);
        })
        .on('mouseout', () => {
          contentG.selectAll(`.${cssId}`).attr('opacity', 0.6).attr('stroke-width', sw);
          hideTooltip();
        })
        .on('click', (event) => {
          event.stopPropagation();
          if (link.type === 'suggested' && link.findingId && link.actionId) {
            onCorrelate(link.findingId, link.actionId);
          } else if (link.type === 'confirmed' && link.findingId && onUncorrelate) {
            if (window.confirm(t('purpleTeam.confirmUnlink') || 'Remove this correlation?')) {
              onUncorrelate(link.findingId);
            }
          }
        });

      if (link.type === 'suggested' && link.score) {
        const midY = (y1 + y2) / 2;
        pathsG.append('text')
          .attr('x', centerX).attr('y', midY - 4)
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px').attr('font-weight', '600')
          .attr('fill', '#f59e0b')
          .style('pointer-events', 'none')
          .text(`${link.score}%`);
      }
    });

    // --- Cards ---
    const cardsG = contentG.append('g').attr('class', 'cards');

    // Track drag state for correlations
    let dragState = null;

    const renderCard = (pos, type) => {
      const item = pos.item;
      const g = cardsG.append('g')
        .attr('class', 'timeline-card')
        .attr('transform', `translate(${pos.x}, ${pos.y})`)
        .style('cursor', 'pointer')
        .datum({ item, type, pos });

      let borderColor, badgeText;
      const bgColor = darkMode ? '#1f2937' : '#fff';

      if (type === 'red') {
        borderColor = item.result === 'successful' ? '#ef4444'
          : item.result === 'detected' ? '#eab308'
          : item.result === 'blocked' ? '#f97316' : '#6b7280';
        const colors = { successful: ['#fef2f2','#991b1b'], detected: ['#fefce8','#854d0e'], blocked: ['#fff7ed','#9a3412'] };
        const c = colors[item.result] || ['#f3f4f6','#374151'];
        badgeText = { text: item.result || 'N/A', bg: c[0], fg: c[1] };
      } else if (type === 'web') {
        borderColor = item.severity === 'critical' ? '#ef4444'
          : item.severity === 'high' ? '#f97316'
          : item.severity === 'medium' ? '#eab308'
          : item.severity === 'low' ? '#22c55e' : '#06b6d4';
        const sev = item.severity || 'info';
        const colors = { critical: ['#ecfeff','#155e75'], high: ['#ecfeff','#155e75'], medium: ['#ecfeff','#155e75'], low: ['#ecfeff','#155e75'], info: ['#ecfeff','#155e75'] };
        const c = colors[sev] || colors.info;
        badgeText = { text: sev, bg: c[0], fg: c[1] };
      } else {
        borderColor = item.severity === 'critical' ? '#ef4444'
          : item.severity === 'high' ? '#f97316'
          : item.severity === 'medium' ? '#eab308'
          : item.severity === 'low' ? '#22c55e' : '#3b82f6';
        const sev = item.severity || 'info';
        const colors = { critical: ['#fef2f2','#991b1b'], high: ['#fff7ed','#9a3412'], medium: ['#fefce8','#854d0e'], low: ['#f0fdf4','#166534'], info: ['#eff6ff','#1e40af'] };
        const c = colors[sev] || colors.info;
        badgeText = { text: sev, bg: c[0], fg: c[1] };
      }

      // Shadow
      g.append('rect').attr('x', 1).attr('y', 2)
        .attr('width', CARD_WIDTH).attr('height', CARD_HEIGHT)
        .attr('rx', 8).attr('fill', 'rgba(0,0,0,0.08)');

      // Main rect
      const mainRect = g.append('rect')
        .attr('width', CARD_WIDTH).attr('height', CARD_HEIGHT)
        .attr('rx', 8).attr('fill', bgColor)
        .attr('stroke', borderColor).attr('stroke-width', 2);

      // Accent bar
      g.append('rect').attr('width', 4).attr('height', CARD_HEIGHT)
        .attr('rx', 2).attr('fill', borderColor);

      // Title
      const titleText = (item.title || 'Untitled').substring(0, 28) + ((item.title || '').length > 28 ? '...' : '');
      g.append('text').attr('x', 12).attr('y', 20)
        .attr('font-size', '12px').attr('font-weight', '600')
        .attr('fill', darkMode ? '#e5e7eb' : '#1f2937')
        .style('pointer-events', 'none')
        .text(titleText);

      // Technique / WSTG info
      const technique = item.technique || item.findingData?.suspectedTechnique;
      const wstgId = item.wstgTestId || item.findingData?.wstgTest?.testId;
      if (technique?.id) {
        g.append('text').attr('x', 12).attr('y', 36)
          .attr('font-size', '10px')
          .attr('fill', darkMode ? '#9ca3af' : '#6b7280')
          .style('pointer-events', 'none')
          .text(technique.id + (technique.name ? ' - ' + technique.name.substring(0, 18) : ''));
      } else if (type === 'web' && wstgId) {
        g.append('text').attr('x', 12).attr('y', 36)
          .attr('font-size', '10px')
          .attr('fill', '#06b6d4')
          .style('pointer-events', 'none')
          .text(wstgId + (item.endpoint ? ' ' + (item.endpoint.substring(0, 20)) : ''));
      }

      // Timestamp
      g.append('text').attr('x', 12).attr('y', 52)
        .attr('font-size', '9px')
        .attr('fill', darkMode ? '#6b7280' : '#9ca3af')
        .style('pointer-events', 'none')
        .text(d3.timeFormat('%b %d, %H:%M:%S')(new Date(item.timestamp)));

      // Badge
      g.append('rect').attr('x', CARD_WIDTH - 68).attr('y', 40)
        .attr('width', 58).attr('height', 18).attr('rx', 9).attr('fill', badgeText.bg);
      g.append('text').attr('x', CARD_WIDTH - 39).attr('y', 53)
        .attr('text-anchor', 'middle').attr('font-size', '9px').attr('font-weight', '600')
        .attr('fill', badgeText.fg).style('pointer-events', 'none')
        .text(badgeText.text);

      // Correlated indicator
      if (type === 'blue' && item.relatedAction) {
        g.append('circle').attr('cx', CARD_WIDTH - 14).attr('cy', 14).attr('r', 7).attr('fill', '#a855f7');
        g.append('text').attr('x', CARD_WIDTH - 14).attr('y', 18)
          .attr('text-anchor', 'middle').attr('font-size', '9px').attr('fill', '#fff')
          .style('pointer-events', 'none').text('\u21C4');
      }

      // Drag grip indicator (small icon area on right side)
      g.append('rect')
        .attr('x', CARD_WIDTH - 20).attr('y', CARD_HEIGHT - 20)
        .attr('width', 16).attr('height', 16)
        .attr('rx', 3).attr('fill', 'transparent');
      // 3 dots drag indicator
      [0, 5, 10].forEach(dy => {
        g.append('circle')
          .attr('cx', CARD_WIDTH - 12).attr('cy', CARD_HEIGHT - 16 + dy)
          .attr('r', 1.5)
          .attr('fill', darkMode ? '#4b5563' : '#d1d5db')
          .style('pointer-events', 'none');
      });

      // --- Drag behavior for card-to-card correlation ---
      const dragBehavior = d3.drag()
        .on('start', function(event) {
          event.sourceEvent.stopPropagation(); // Prevent zoom
          const d = d3.select(this).datum();
          dragState = {
            sourceType: d.type,
            sourceItem: d.item,
            sourcePos: d.pos,
            startX: d.pos.x + CARD_WIDTH / 2,
            startY: d.pos.y + CARD_HEIGHT / 2
          };
          hideTooltip();
          dragLine
            .attr('x1', dragState.startX)
            .attr('y1', dragState.startY)
            .attr('x2', dragState.startX)
            .attr('y2', dragState.startY)
            .attr('opacity', 0.8);
        })
        .on('drag', function(event) {
          if (!dragState) return;
          // Get position in content coordinate space
          const transform = d3.zoomTransform(svgRef.current);
          const mx = (event.sourceEvent.offsetX - transform.x) / transform.k;
          const my = (event.sourceEvent.offsetY - transform.y) / transform.k;
          dragLine.attr('x2', mx).attr('y2', my);

          // Check if hovering over a valid target card
          const targetType = dragState.sourceType === 'red' ? 'blue' : 'red';
          const targetPositions = targetType === 'red' ? redPositions : bluePositions;
          let hoverTarget = null;
          for (const tp of targetPositions) {
            if (mx >= tp.x && mx <= tp.x + tp.w && my >= tp.y && my <= tp.y + tp.h) {
              hoverTarget = tp;
              break;
            }
          }
          if (hoverTarget) {
            dropHighlight
              .attr('x', hoverTarget.x - 3).attr('y', hoverTarget.y - 3)
              .attr('width', hoverTarget.w + 6).attr('height', hoverTarget.h + 6)
              .attr('opacity', 1);
          } else {
            dropHighlight.attr('opacity', 0);
          }
        })
        .on('end', function(event) {
          dragLine.attr('opacity', 0);
          dropHighlight.attr('opacity', 0);
          if (!dragState) return;

          // Find drop target
          const transform = d3.zoomTransform(svgRef.current);
          const mx = (event.sourceEvent.offsetX - transform.x) / transform.k;
          const my = (event.sourceEvent.offsetY - transform.y) / transform.k;

          const targetType = dragState.sourceType === 'red' ? 'blue' : 'red';
          const targetPositions = targetType === 'red' ? redPositions : bluePositions;

          for (const tp of targetPositions) {
            if (mx >= tp.x && mx <= tp.x + tp.w && my >= tp.y && my <= tp.y + tp.h) {
              // Found drop target — create correlation
              const redItem = dragState.sourceType === 'red' ? dragState.sourceItem : tp.item;
              const blueItem = dragState.sourceType === 'blue' ? dragState.sourceItem : tp.item;

              const findingId = idStr(blueItem.findingData?._id) || blueItem._sid;
              const actionId = idStr(redItem.actionData?._id) || redItem._sid;

              if (findingId && actionId) {
                onCorrelate(findingId, actionId);
              }
              break;
            }
          }

          dragState = null;
        });

      g.call(dragBehavior);

      // Hover + Click (still works — drag only activates on actual movement)
      g.on('mouseover', (event) => {
        if (dragState) return; // Don't show tooltip while dragging
        mainRect.attr('stroke-width', 3);
        // Highlight related paths
        contentG.selectAll('path[class]').each(function() {
          const cls = d3.select(this).attr('class') || '';
          if (type === 'red' && cls.startsWith(`corr-${item._sid}-`)) {
            d3.select(this).attr('opacity', 1).attr('stroke-width', 4);
          } else if (type === 'blue' && cls.endsWith(`-${item._sid}`.replace(/[^a-zA-Z0-9-]/g, '_'))) {
            d3.select(this).attr('opacity', 1).attr('stroke-width', 4);
          }
        });

        let html = '';
        const ts = new Date(item.timestamp);
        if (type === 'red') {
          html = `<div style="font-weight:600;margin-bottom:6px">${item.title}</div>`;
          if (item.technique) html += `<div>Technique: ${item.technique.id} - ${item.technique.name}</div>`;
          html += `<div>Result: <span style="color:${borderColor};font-weight:600">${item.result || 'N/A'}</span></div>`;
          if (item.operator) html += `<div>Operator: ${item.operator}</div>`;
          html += `<div style="color:${darkMode ? '#9ca3af' : '#6b7280'};margin-top:4px">${d3.timeFormat('%b %d, %Y %H:%M:%S')(ts)}</div>`;
          if (item.actionData?.description) {
            html += `<div style="margin-top:6px;font-size:11px;color:${darkMode ? '#9ca3af' : '#6b7280'}">${item.actionData.description.substring(0, 140)}${item.actionData.description.length > 140 ? '...' : ''}</div>`;
          }
          html += `<div style="margin-top:6px;font-size:10px;color:#a855f7">${t('purpleTeam.dragToCorrelate') || 'Drag to blue card to correlate'} | Click for details</div>`;
        } else {
          html = `<div style="font-weight:600;margin-bottom:6px">${item.title}</div>`;
          const bt = item.findingData?.suspectedTechnique;
          if (bt) html += `<div>Technique: ${bt.id}${bt.name ? ' - ' + bt.name : ''}</div>`;
          html += `<div>Severity: <span style="color:${borderColor};font-weight:600">${item.severity || 'N/A'}</span></div>`;
          if (item.source) html += `<div>Source: ${item.source.replace(/_/g, ' ')}</div>`;
          html += `<div style="color:${darkMode ? '#9ca3af' : '#6b7280'};margin-top:4px">${d3.timeFormat('%b %d, %Y %H:%M:%S')(ts)}</div>`;
          if (item.findingData?.description) {
            html += `<div style="margin-top:6px;font-size:11px;color:${darkMode ? '#9ca3af' : '#6b7280'}">${item.findingData.description.substring(0, 140)}${item.findingData.description.length > 140 ? '...' : ''}</div>`;
          }
          if (item.relatedAction) html += `<div style="margin-top:4px;color:#a855f7;font-size:11px">${t('purpleTeam.linkedToAction') || 'Linked to red team action'}</div>`;
          html += `<div style="margin-top:6px;font-size:10px;color:#a855f7">${t('purpleTeam.dragToCorrelate') || 'Drag to red card to correlate'} | Click for details</div>`;
        }
        showTooltip(event, html);
      })
      .on('mouseout', () => {
        mainRect.attr('stroke-width', 2);
        contentG.selectAll('path[class]').each(function() {
          const el = d3.select(this);
          const cls = el.attr('class') || '';
          if (cls.startsWith('corr-')) {
            el.attr('opacity', 0.6);
            const s = el.attr('stroke');
            el.attr('stroke-width', s === '#a855f7' ? 2.5 : s === '#f59e0b' ? 2 : 1);
          }
        });
        hideTooltip();
      })
      .on('click', (event) => {
        // Only fire click if not a drag (d3.drag sets event.defaultPrevented on drag)
        if (event.defaultPrevented) return;
        event.stopPropagation();
        hideTooltip();
        if (type === 'red') onActionClick(item.actionData || item);
        else if (type === 'blue') onFindingClick(item.findingData || item);
        else if (type === 'web') onFindingClick(item.findingData || item);
      });
    };

    redPositions.forEach(pos => renderCard(pos, 'red'));
    bluePositions.forEach(pos => renderCard(pos, 'blue'));
    webPositions.forEach(pos => renderCard(pos, 'web'));

    // --- Lane headers (fixed overlay) ---
    const headerG = svg.append('g').attr('class', 'headers');

    headerG.append('rect').attr('x', 0).attr('y', 0)
      .attr('width', laneRight).attr('height', 26)
      .attr('fill', darkMode ? 'rgba(127,29,29,0.6)' : 'rgba(254,226,226,0.92)');
    headerG.append('text').attr('x', laneRight / 2).attr('y', 17)
      .attr('text-anchor', 'middle').attr('font-size', '11px').attr('font-weight', '600')
      .attr('fill', darkMode ? '#fca5a5' : '#991b1b')
      .text(t('purpleTeam.redTeamAction') || 'Red Team Actions');

    headerG.append('rect').attr('x', blueLaneLeft).attr('y', 0)
      .attr('width', width - blueLaneLeft).attr('height', 26)
      .attr('fill', darkMode ? 'rgba(30,58,138,0.6)' : 'rgba(219,234,254,0.92)');
    headerG.append('text').attr('x', (blueLaneLeft + width) / 2).attr('y', 17)
      .attr('text-anchor', 'middle').attr('font-size', '11px').attr('font-weight', '600')
      .attr('fill', darkMode ? '#93c5fd' : '#1e40af')
      .text(t('purpleTeam.blueTeamDetection') || 'Blue Team Detections');

    headerG.append('rect').attr('x', centerStart).attr('y', 0)
      .attr('width', centerEnd - centerStart).attr('height', 26)
      .attr('fill', darkMode ? 'rgba(55,65,81,0.8)' : 'rgba(243,244,246,0.92)');
    headerG.append('text').attr('x', centerX).attr('y', 17)
      .attr('text-anchor', 'middle').attr('font-size', '9px')
      .attr('fill', darkMode ? '#9ca3af' : '#6b7280')
      .text(t('common.time') || 'Time');

    // Web test lane header (only when present)
    if (hasWebTests) {
      headerG.append('rect').attr('x', webLaneLeft).attr('y', 0)
        .attr('width', width - webLaneLeft).attr('height', 26)
        .attr('fill', darkMode ? 'rgba(8,145,178,0.6)' : 'rgba(207,250,254,0.92)');
      headerG.append('text').attr('x', (webLaneLeft + width) / 2).attr('y', 17)
        .attr('text-anchor', 'middle').attr('font-size', '11px').attr('font-weight', '600')
        .attr('fill', darkMode ? '#67e8f9' : '#155e75')
        .text(t('webTesting.webTests') || 'Web Tests');
    }

    // --- Minimap ---
    const mmG = svg.append('g')
      .attr('transform', `translate(${width - MINIMAP_W - 12}, ${height - MINIMAP_H - 36})`);

    mmG.append('rect').attr('width', MINIMAP_W).attr('height', MINIMAP_H).attr('rx', 4)
      .attr('fill', darkMode ? 'rgba(31,41,55,0.92)' : 'rgba(255,255,255,0.92)')
      .attr('stroke', darkMode ? '#4b5563' : '#d1d5db');

    const mmSX = MINIMAP_W / width;
    const mmSY = MINIMAP_H / finalHeight;

    redPositions.forEach(p => {
      mmG.append('rect')
        .attr('x', p.x * mmSX + 1).attr('y', p.y * mmSY + 1)
        .attr('width', Math.max(3, CARD_WIDTH * mmSX)).attr('height', Math.max(2, CARD_HEIGHT * mmSY))
        .attr('rx', 1).attr('fill', '#ef4444').attr('opacity', 0.7);
    });
    bluePositions.forEach(p => {
      mmG.append('rect')
        .attr('x', p.x * mmSX + 1).attr('y', p.y * mmSY + 1)
        .attr('width', Math.max(3, CARD_WIDTH * mmSX)).attr('height', Math.max(2, CARD_HEIGHT * mmSY))
        .attr('rx', 1).attr('fill', '#3b82f6').attr('opacity', 0.7);
    });
    webPositions.forEach(p => {
      mmG.append('rect')
        .attr('x', p.x * mmSX + 1).attr('y', p.y * mmSY + 1)
        .attr('width', Math.max(3, CARD_WIDTH * mmSX)).attr('height', Math.max(2, CARD_HEIGHT * mmSY))
        .attr('rx', 1).attr('fill', '#06b6d4').attr('opacity', 0.7);
    });

    const vpRect = mmG.append('rect')
      .attr('width', MINIMAP_W).attr('height', MINIMAP_H).attr('rx', 2)
      .attr('fill', 'rgba(168,85,247,0.08)').attr('stroke', '#a855f7').attr('stroke-width', 1.5)
      .style('cursor', 'grab');

    let currentTransform = d3.zoomIdentity;
    const updateMinimap = (transform) => {
      currentTransform = transform;
      const vx = -transform.x / transform.k * mmSX;
      const vy = -transform.y / transform.k * mmSY;
      const vw = width / transform.k * mmSX;
      const vh = height / transform.k * mmSY;
      vpRect.attr('x', Math.max(0, vx)).attr('y', Math.max(0, vy))
        .attr('width', Math.min(MINIMAP_W, vw)).attr('height', Math.min(MINIMAP_H, vh));
    };

    // Restore previous zoom transform (keeps position on data refresh)
    if (transformRef.current) {
      svg.call(zoomBehavior.transform, transformRef.current);
    }

    // Drag on viewport rectangle to pan the main canvas
    let dragStartTransform = null;
    const vpDrag = d3.drag()
      .on('start', (event) => {
        event.sourceEvent.stopPropagation();
        dragStartTransform = { x: currentTransform.x, y: currentTransform.y, k: currentTransform.k };
        vpRect.style('cursor', 'grabbing');
      })
      .on('drag', (event) => {
        if (!dragStartTransform) return;
        // Convert minimap delta to content-space delta
        const dx = event.dx / mmSX * dragStartTransform.k;
        const dy = event.dy / mmSY * dragStartTransform.k;
        const newTx = currentTransform.x - dx;
        const newTy = currentTransform.y - dy;
        const newTransform = d3.zoomIdentity.translate(newTx, newTy).scale(currentTransform.k);
        svg.call(zoomBehavior.transform, newTransform);
      })
      .on('end', () => {
        dragStartTransform = null;
        vpRect.style('cursor', 'grab');
      });
    vpRect.call(vpDrag);

    // Click on minimap background to jump to that position
    mmG.on('click', (event) => {
      if (event.defaultPrevented) return; // skip if drag just ended
      event.stopPropagation();
      const [mx, my] = d3.pointer(event);
      const k = currentTransform.k;
      const tx = -(mx / mmSX * k - width / 2);
      const ty = -(my / mmSY * k - height / 2);
      svg.transition().duration(300).call(zoomBehavior.transform, d3.zoomIdentity.translate(tx, ty).scale(k));
    });

    // --- Legend ---
    const legendG = svg.append('g').attr('transform', `translate(12, ${height - 24})`);
    legendG.append('rect').attr('x', -6).attr('y', -10).attr('width', 380).attr('height', 22).attr('rx', 4)
      .attr('fill', darkMode ? 'rgba(31,41,55,0.88)' : 'rgba(255,255,255,0.88)');

    const legendItems = [
      { color: '#a855f7', dash: 'none', label: t('purpleTeam.confirmed') || 'Confirmed' },
      { color: '#f59e0b', dash: '6,4', label: t('purpleTeam.suggested') || 'Suggested' },
      { color: '#9ca3af', dash: '2,4', label: t('purpleTeam.timeProximity') || 'Time proximity' }
    ];
    if (hasWebTests) {
      legendItems.push({ color: '#06b6d4', dash: 'none', label: t('webTesting.webTests') || 'Web Tests', isBlock: true });
    }
    const legendWidth = legendItems.length * 130;
    legendG.select('rect').attr('width', legendWidth);
    let lx = 0;
    legendItems.forEach(item => {
      if (item.isBlock) {
        legendG.append('rect').attr('x', lx).attr('y', -4).attr('width', 16).attr('height', 8)
          .attr('rx', 2).attr('fill', item.color);
      } else {
        legendG.append('line').attr('x1', lx).attr('y1', 0).attr('x2', lx + 24).attr('y2', 0)
          .attr('stroke', item.color).attr('stroke-width', 2).attr('stroke-dasharray', item.dash);
      }
      legendG.append('text').attr('x', lx + 28).attr('y', 4)
        .attr('font-size', '10px').attr('fill', darkMode ? '#9ca3af' : '#6b7280')
        .text(item.label);
      lx += 130;
    });

    return () => { tooltip.remove(); };
  }, [timeline, suggestions, dimensions, darkMode, t, onCorrelate, onUncorrelate, onActionClick, onFindingClick]);

  useEffect(() => {
    const cleanup = drawTimeline();
    return () => {
      if (cleanup) cleanup();
      d3.selectAll('.corr-tl-tooltip').remove();
    };
  }, [drawTimeline]);

  const handleZoom = (factor) => {
    const svg = d3.select(svgRef.current);
    if (zoomRef.current) svg.transition().duration(200).call(zoomRef.current.scaleBy, factor);
  };

  const handleFit = () => {
    const svg = d3.select(svgRef.current);
    if (zoomRef.current) svg.transition().duration(300).call(zoomRef.current.transform, d3.zoomIdentity);
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Controls */}
      <div className="absolute top-2 right-2 z-10 flex flex-col gap-1">
        {onToggleFullscreen && (
          <button onClick={onToggleFullscreen}
            className={`p-1.5 rounded text-xs shadow border ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border-gray-300'}`}
            title={fullscreen ? (t('purpleTeam.exitFullscreen') || 'Exit fullscreen') : (t('purpleTeam.fullscreen') || 'Fullscreen')}>
            <FontAwesomeIcon icon={fullscreen ? faCompress : faExpand} />
          </button>
        )}
        <button onClick={handleFit}
          className={`p-1.5 rounded text-xs shadow border ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border-gray-300'}`}
          title={t('purpleTeam.fitView') || 'Fit to view'}>
          <FontAwesomeIcon icon={faExpand} />
        </button>
        <button onClick={() => handleZoom(1.4)}
          className={`p-1.5 rounded text-xs shadow border ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border-gray-300'}`}
          title="Zoom in">
          <FontAwesomeIcon icon={faSearchPlus} />
        </button>
        <button onClick={() => handleZoom(0.7)}
          className={`p-1.5 rounded text-xs shadow border ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border-gray-300'}`}
          title="Zoom out">
          <FontAwesomeIcon icon={faSearchMinus} />
        </button>
      </div>
      <svg ref={svgRef} width={dimensions.width} height={dimensions.height}
        style={{ display: 'block', userSelect: 'none' }} />
    </div>
  );
};

export default CorrelationTimeline;
